import { Modal, Button, Table, Input } from "antd";
import { useEffect, useRef } from "react";
import styled from "styled-components";

const { TextArea } = Input;
const StyledSpan = styled.span`
  white-space: pre-wrap;
`;
const StyledSpeakerSpan = styled(StyledSpan)`
  color: ${(props) => props?.color || "inherit"};
  font-weight: bold;
`;

const StyledTextArea = styled(TextArea)`
  resize: none;
  width: 100%;
`;
const StyledHeader = styled.h4`
  font-weight: 500;
  margin-bottom: 8px;
`;

const StyledTable = styled(Table)`
  margin-top: 8px;
  & .ant-table-container {
    max-height: calc(100vh - 240px);
    overflow: scroll;
  }
  .ant-table-tbody > tr > td {
    border: none;
    vertical-align: top;
    padding: 2px;
  }
`;

const StyledModal = styled(Modal)`
  max-width: 800px;
  .ant-modal-body {
    overflow-y: auto;
    flex-direction: column;
  }
`;

function MediaPlayer({ url }) {
  const audioRef = useRef(null);
  useEffect(() => {
    if (audioRef.current && url) {
      audioRef.current.src = url;
      audioRef.current.load();
    }
  }, [url]);

  return (
    <audio controls ref={audioRef} style={{ width: "100%" }}>
      <track default kind="captions" srcLang="en" />
      <source src={url} type="audio/mpeg" />
      <source src={url} type="audio/x-m4a" />
      <source src={url} type="audio/mp4" />
      <source src={url} type="audio/aac" />
      <source src={url} type="audio/webm" />
      <source src={url} type="audio/wav" />
      <source src={url} type="audio/ogg" />
      Your browser does not support the audio element.
    </audio>
  );
}

function NotesModal({ title, open, onClose, record }) {
  const speakerTypeToColorMap = {
    agent: "black",
    recipient: "#1677ff",
    ai_agent: "black",
    other: "purple",
  };
  const speakerTypeToLabelMap = {
    agent: "Agent",
    recipient: "Recipient",
    ai_agent: "AI Agent",
    other: "Other",
  };
  const columns = [
    {
      title: "Speaker",
      dataIndex: "speakerType",
      key: "speakerType",
      render: (speakerType, row) => {
        return (
          <StyledSpeakerSpan color={speakerTypeToColorMap[row.speakerType]}>
            {speakerTypeToLabelMap[row.speakerType]}
          </StyledSpeakerSpan>
        );
      },
    },
    {
      title: "Transcription",
      dataIndex: "text",
      key: "text",
      render: (text) => <StyledSpan>{text}</StyledSpan>,
    },
  ];

  const hasCallRecording = record?.callRecordingUrl && record?.callRecordingUrl !== "";
  const hasCallTranscription =
    record?.callTranscription &&
    Array.isArray(record.callTranscription) &&
    record.callTranscription.length > 0;
  const notesRowToShow = !hasCallTranscription ? 10 : 5;

  return (
    <StyledModal
      footer={[
        <Button type="primary" key="close" onClick={onClose}>
          Close
        </Button>,
      ]}
      maskClosable={false}
      title={title}
      open={open}
      onCancel={onClose}
      onOk={onClose}
      // This is needed to dismount the MediaPlayer component on close.
      // Otherwise audio will keep playing in the background after modal is closed.
      destroyOnClose
    >
      <>
        <StyledHeader>Notes</StyledHeader>
        <StyledTextArea value={record?.notes} rows={notesRowToShow} readOnly />
      </>
      {hasCallRecording && (
        <>
          <StyledHeader>Call Recording</StyledHeader>
          <MediaPlayer url={record?.callRecordingUrl} />
        </>
      )}
      {hasCallTranscription && (
        <>
          <StyledHeader>Transcripts</StyledHeader>
          <StyledTable
            showHeader={false}
            dataSource={record?.callTranscription}
            columns={columns}
            pagination={false}
            scroll={{ x: "auto", y: record?.callTranscription.length > 0 ? 400 : null }}
          />
        </>
      )}
    </StyledModal>
  );
}

export default NotesModal;
